import type { MouseEvent, ReactNode } from 'react';
import type { CardView, NewsPayload } from '../api';
import { coverageHint } from '../coverage';
import { latestPublished, sourceNames, uncertainties } from '../briefing';
import { relativeAge } from '../format';
import { useOpenDetail } from './detail-link';
import { ChangeLine } from './editions';
import { InterestButtons } from './feedback';
import { FollowButton, SubjectChips } from './follow';
import { RecordNotes, useCardLabels } from './sources';

/**
 * A click on the card's non-interactive surface opens its details, as the title
 * does. Controls and links keep their own action, and a click that ends a text
 * selection does not open anything.
 */
export function useSurfaceOpen(versionId: string) {
  const open = useOpenDetail();
  return (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea, summary, label, [role="button"]')) return;
    const selection = window.getSelection?.();
    if (selection && !selection.isCollapsed && selection.toString().trim() !== '') return;
    open(versionId, e.currentTarget.querySelector<HTMLElement>('.title-button'));
  };
}

export function SourceLine({ view, showTopic = false, extra }: { view: CardView; showTopic?: boolean; extra?: ReactNode }) {
  const names = sourceNames(view);
  const published = latestPublished(view);
  const hint = view.version.payload.payload_type === 'news.v1' ? coverageHint(view.version.payload as NewsPayload) : null;
  const labels = useCardLabels(view);
  return (
    <div className="meta">
      {showTopic && view.topic && <span className="tag">{view.topic.label}</span>}
      {labels.map((l) => (
        <span key={l} className="tag tag-role">
          {l}
        </span>
      ))}
      {names.length > 0 && <span className="sources">{names.join(' · ')}</span>}
      <span>
        {published ? (
          <time dateTime={published} title={`Veröffentlicht ${new Date(published).toLocaleString('de-CH')}`}>
            {relativeAge(published)}
          </time>
        ) : (
          'Datum unbekannt'
        )}
      </span>
      {hint && (
        <span className="coverage-hint" title="Wie viel Quellentext gelesen wurde; Details unter „Lesen & Quellen“.">
          {hint}
        </span>
      )}
      {extra}
    </div>
  );
}

function Uncertainty({ view, limit = 1 }: { view: CardView; limit?: number }) {
  const items = uncertainties(view);
  if (items.length === 0) return null;
  const shown = items.slice(0, limit);
  const rest = items.length - shown.length;
  return (
    <div className="uncertain">
      <span className="visually-hidden">Unsicherheit: </span>
      {shown.join(' ')}
      {rest > 0 && <span className="muted"> (+{rest} weitere offene {rest === 1 ? 'Frage' : 'Fragen'})</span>}
    </div>
  );
}

function ReadButton({ view }: { view: CardView }) {
  const open = useOpenDetail();
  const n = sourceNames(view).length;
  return (
    <button
      type="button"
      className="small-button"
      aria-label={`Lesen und Quellen: ${view.version.title}`}
      onClick={(e) => open(view.version.id, e.currentTarget)}
    >
      Lesen &amp; Quellen{n > 0 ? ` (${n})` : ''}
    </button>
  );
}

export function TitleButton({ view, as: Tag = 'h3' }: { view: CardView; as?: 'h2' | 'h3' | 'h4' }) {
  const open = useOpenDetail();
  return (
    <Tag className="story-title">
      <button type="button" className="title-button" lang={view.version.language || undefined} onClick={(e) => open(view.version.id, e.currentTarget)}>
        {view.version.title}
      </button>
    </Tag>
  );
}

/** A news card on the topic board. */
export function StoryCard({ view, showTopic = false }: { view: CardView; showTopic?: boolean }) {
  const onSurface = useSurfaceOpen(view.version.id);
  return (
    <article className="story" data-version={view.version.id} onClick={onSurface}>
      <SourceLine view={view} showTopic={showTopic} extra={view.entry?.score?.exploration_slot ? <span className="tag tag-soft">Entdeckung</span> : undefined} />
      <TitleButton view={view} />
      <SubjectChips view={view} />
      <ChangeLine view={view} />
      <p className="summary">{view.version.summary}</p>
      <RecordNotes view={view} />
      <Uncertainty view={view} />
      <div className="actions">
        <ReadButton view={view} />
        <FollowButton view={view} />
        <InterestButtons view={view} />
      </div>
    </article>
  );
}

/** A compact row for the longer tail: headline, sources, age; opens the same details. */
export function StoryRow({ view, showTopic = false }: { view: CardView; showTopic?: boolean }) {
  const onSurface = useSurfaceOpen(view.version.id);
  const flagged = uncertainties(view).length > 0;
  return (
    <li className="story-row" data-version={view.version.id} onClick={onSurface}>
      <SourceLine
        view={view}
        showTopic={showTopic}
        extra={flagged ? <span className="tag tag-warn">mit Unsicherheit</span> : undefined}
      />
      <TitleButton view={view} as="h4" />
      <SubjectChips view={view} />
      <ChangeLine view={view} compact />
      <RecordNotes view={view} />
    </li>
  );
}
